import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Button,
  Switch,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

import { motion } from "framer-motion";
import { toast } from "react-toastify";
import apiService from "../../../../../../service/Api";
import CustomTextField from "../../../../../../components/common/Textfield";
import GoFundMeWidget from "../../../../../../components/GoFundMeWidget";
import DonationsTable from "../../components/chema/DonationsTable";


interface StreamResponse {
  id: number;
  booking: {
    id: number;
  } | null;
}

interface DonationsTabProps {
  streamData: {
    go_fund_me_link?: string;
    go_fund_me_enabled: boolean;
  } | null;
  streamId: string;
  isAdmin: boolean;
  isMobile: boolean;
  fetchStreamDetails: () => Promise<void>;
}

export default function DonationsTab({
  streamData,
  streamId,
  isAdmin,
  isMobile,
  fetchStreamDetails,
}: DonationsTabProps) {
  

  const [goFundMeLink, setGoFundMeLink] = useState(
    streamData?.go_fund_me_link || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [loadingBookingId, setLoadingBookingId] = useState(true);

  const handleSaveLink = async () => {
    if (!goFundMeLink || !goFundMeLink.includes("gofundme.com")) {
      setError("Please enter a valid GoFundMe URL");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const payload = { go_fund_me_link: goFundMeLink };
      await apiService.patch(`/api/streams/stream/${streamId}/`, payload);
      toast.success("GoFundMe link saved successfully");
      await fetchStreamDetails();
    } catch (err: any) {
      console.error("Failed to save link:", err);
      let errorMessage = "Failed to save GoFundMe link";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGoFundMeEnabled = async (newValue: boolean) => {
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        go_fund_me_enabled: newValue,
      });
      toast.success(
        `Donations ${newValue ? "enabled" : "disabled"} successfully`
      );
      await fetchStreamDetails();
    } catch (err) {
      console.error("Failed to toggle donation setting:", err);
      toast.error("Failed to update donation setting.");
    }
  };

  useEffect(() => {
    const fetchBookingId = async () => {
      if (!streamId) return;

      setLoadingBookingId(true);
      try {
        const response = await apiService.get<StreamResponse>(
          `/api/streams/stream/${streamId}/`
        );

        if (response.data?.booking?.id) {
          setBookingId(response.data.booking.id);
        }
      } catch (err) {
        console.error("Failed to fetch stream details:", err);
      } finally {
        setLoadingBookingId(false);
      }
    };

    fetchBookingId();
  }, [streamId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ height: "100%", boxShadow: 0, borderRadius: "20px" }}>
        <CardContent>
          {/* Admin Section */}
          {isAdmin && (
            <Box sx={{ mb: 3 }}>
              <CustomTextField
                label="GoFundMe Embed URL"
                value={goFundMeLink}
                onChange={(e) => {
                  setGoFundMeLink(e.target.value);
                }}
                fullWidth
                margin="normal"
                placeholder="https://www.gofundme.com/f/campaign-name or widget URL"
                helperText="Enter the GoFundMe campaign URL or widget embed URL"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleSaveLink}
                        variant="contained"
                        size="small"
                        disabled={isSaving}
                      >
                        {isSaving
                          ? "Saving..."
                          : streamData?.go_fund_me_link
                          ? "Update"
                          : "Save"}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Typography sx={{ mr: 2 }}>Enable Donations</Typography>
                <Switch
                  checked={streamData?.go_fund_me_enabled || false}
                  onChange={(e) =>
                    handleToggleGoFundMeEnabled(e.target.checked)
                  }
                  color="primary"
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {streamData?.go_fund_me_link && isMobile && (
            <Box sx={{ mt: 4 }}>
              <GoFundMeWidget url={streamData.go_fund_me_link} />
            </Box>
          )}
          
        </CardContent>
        {streamData?.go_fund_me_enabled && bookingId && !loadingBookingId && (
          <DonationsTable
            bookingId={bookingId}
            title="All Donations"
            ordering="-created_at"
          />
        )}
        {streamData?.go_fund_me_enabled && loadingBookingId && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}

      </Card>
    </motion.div>
  );
}
